import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { MessagesSquare } from "lucide-react";
import { useMemo, useState } from "react";
import { z } from "zod";
import { usersQuery } from "../../../api/admin";
import { type ConversationQuery, conversationsInfinite, type SmsConversation } from "../../../api/messaging";
import { DataTable } from "../../../components/DataTable";
import { Badge, Button, Card, EmptyState, ErrorState, Input, Loading, Select } from "../../../components/ui";
import { fmtDateTime, fmtPhone } from "../../../lib/format";
import { useNavigate } from "../../../lib/navigate";
import { useDebounced } from "../../../lib/useDebounced";

const searchSchema = z.object({
  user: z.string().optional().catch(undefined),
  unread: z.boolean().optional().catch(undefined),
  q: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/admin/messages/")({
  staticData: { crumb: { label: "Messages", parents: [] } },
  validateSearch: (s) => searchSchema.parse(s),
  component: ConversationsPage,
});

const col = createColumnHelper<SmsConversation>();

function contactLabel(c: SmsConversation): string {
  return c.contact?.name ?? fmtPhone(c.contact_number);
}

function ConversationsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const users = useQuery(usersQuery);
  const [text, setText] = useState(search.q ?? "");
  const q = useDebounced(text.trim(), 250);
  const query: ConversationQuery = useMemo(
    () => ({
      filter: search.unread ? "unread" : "all",
      user_id: search.user,
      q: q || undefined,
    }),
    [search.unread, search.user, q],
  );
  const list = useInfiniteQuery(conversationsInfinite(query));
  const rows = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);
  const set = (patch: Partial<z.infer<typeof searchSchema>>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const filtered = Boolean(search.unread || search.user || q);

  const columns = useMemo(
    () => [
      col.accessor(contactLabel, {
        id: "contact",
        header: "Contact",
        cell: (c) => {
          const r = c.row.original;
          return (
            <span className="flex min-w-0 items-center gap-2">
              <span className={r.unread_count > 0 ? "font-medium" : undefined}>{c.getValue()}</span>
              {r.contact?.company ? <span className="truncate text-ink-3">{r.contact.company}</span> : null}
              {r.opted_out ? <Badge tone="danger">Opted out</Badge> : null}
            </span>
          );
        },
      }),
      col.accessor("contact_number", {
        header: "Number",
        cell: (c) => <span className="tnum whitespace-nowrap">{fmtPhone(c.getValue())}</span>,
      }),
      col.accessor((r) => r.assigned_to?.name ?? "", {
        id: "rep",
        header: "Rep",
        cell: (c) => c.getValue() || <span className="text-ink-3">Unassigned</span>,
      }),
      col.accessor("last_message_preview", {
        header: "Last message",
        enableSorting: false,
        cell: (c) => (
          <span className="block max-w-[28rem] truncate text-ink-2">
            {c.row.original.last_direction === "outbound" ? "You: " : ""}
            {c.getValue() ?? ""}
          </span>
        ),
      }),
      col.accessor("last_message_at", {
        header: "When",
        cell: (c) => <span className="tnum whitespace-nowrap">{fmtDateTime(c.getValue())}</span>,
      }),
      col.accessor("unread_count", {
        header: "Unread",
        meta: { align: "right" },
        cell: (c) => (c.getValue() > 0 ? <Badge tone="accent">{c.getValue()}</Badge> : null),
      }),
    ],
    [],
  );

  return (
    <>
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <Input
          type="search"
          aria-label="Search conversations"
          placeholder="Name, company or number"
          className="w-64"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <Select
          aria-label="Rep"
          className="w-44"
          value={search.user ?? ""}
          onValueChange={(v) => set({ user: v || undefined })}
          options={[
            { value: "", label: "Every rep" },
            ...(users.data ?? []).map((u) => ({ value: u.id, label: u.name })),
          ]}
        />
        <Button
          variant={search.unread ? "tinted" : "secondary"}
          aria-pressed={Boolean(search.unread)}
          onClick={() => set({ unread: search.unread ? undefined : true })}
        >
          Unread only
        </Button>
        {filtered ? (
          <Button
            variant="ghost"
            onClick={() => {
              setText("");
              navigate({ search: {}, replace: true });
            }}
          >
            Clear
          </Button>
        ) : null}
      </div>
      <Card>
        {list.isPending ? (
          <Loading />
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => list.refetch()} />
        ) : (
          <>
            <DataTable
              data={rows}
              columns={columns}
              getRowId={(r) => r.id}
              onRowClick={(r) =>
                navigate({ to: "/admin/messages/$conversationId", params: { conversationId: r.id } })
              }
              rowLabel={(r) => `Conversation with ${contactLabel(r)}`}
              empty={
                <EmptyState
                  icon={<MessagesSquare />}
                  title={filtered ? "No conversations match" : "No texts yet"}
                  {...(filtered ? { description: "Clear the filters to see every conversation." } : {})}
                />
              }
            />
            {list.hasNextPage ? (
              <div className="flex justify-center border-hairline border-t p-3">
                <Button size="sm" onClick={() => list.fetchNextPage()} busy={list.isFetchingNextPage}>
                  Load more
                </Button>
              </div>
            ) : null}
          </>
        )}
      </Card>
    </>
  );
}
